/**
 * @file coupon-form-types.ts
 *
 * Shared types and constants for the coupon form: the discount-type union and its
 * ordered list (driving the native `<select>`), the editable field set, and the
 * validation-error keys. Centralised here (mirroring the products tab's
 * `product-form-types.ts`) so the form hook, the pure helpers, and the view all
 * agree on one shape.
 */

/** The three WooCommerce discount types this UI supports. */
export type DiscountType = 'percent' | 'fixed_cart' | 'fixed_product';

/** The discount types in display order, driving the form's `<select>`. */
export const COUPON_DISCOUNT_TYPES: readonly DiscountType[] = [
  'percent',
  'fixed_cart',
  'fixed_product',
];

/**
 * The editable fields of the coupon form. Every value is a string (or boolean
 * for the toggles); the numeric/amount fields are parsed and conditionally
 * included when the payload is assembled.
 */
export interface CouponFormFields {
  code: string;
  discount_type: DiscountType;
  amount: string;
  description: string;
  /** `YYYY-MM-DD` (the native date input value), or `""` when never expiring. */
  date_expires: string;
  usage_limit: string;
  minimum_amount: string;
  maximum_amount: string;
  individual_use: boolean;
  free_shipping: boolean;
}

/** Validation errors keyed by field. */
export interface CouponFormErrors {
  codeError?: string;
  amountError?: string;
  dateExpiresError?: string;
  usageLimitError?: string;
  minimumAmountError?: string;
  maximumAmountError?: string;
}
